<template>
	<v-form ref="form">
		<v-card-text v-if="loadingCounter > 0">
			<v-progress-linear indeterminate />
		</v-card-text>

		<template v-else>
			<v-card-text v-if="statusText">
				<v-subheader class="subtitle-1">{{statusText}}</v-subheader>
			</v-card-text>

			<v-card-text v-if="userList.length > 0">
				<v-list>
					<v-subheader class="subtitle-1">{{$t('Auth.SelectUserAccount')}}</v-subheader>
					<v-list-item v-for="item of userList" @click="selectUser(item)" :key="item.Login">
						<v-list-item-content>
							<v-list-item-title>{{item.Login}}</v-list-item-title>
							<v-list-item-subtitle>{{$formatter.getRollText(item.Roll)}}</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action>
							<Tooltip :text="$t('Auth.SelectUserAccount')">
								<v-btn icon @click="onSelectUser(item)"><v-icon>mdi-login</v-icon></v-btn>
							</Tooltip>
						</v-list-item-action>
					</v-list-item>
				</v-list>
			</v-card-text>

			<v-card-actions v-if="showLoginButton">
				<v-btn block color="secondary" :href="authUrl">{{provider?.signInText}}</v-btn>
			</v-card-actions>
			
			<v-card-actions v-else-if="provider">
				<v-btn block color="secondary" @click="onCancel()">{{$t('Cancel')}}</v-btn>
			</v-card-actions>
		</template>
	</v-form>
</template>

<script>
import {mapActions} from 'vuex';
import Tooltip      from '../Tooltip.vue';

export default {
	components: {
		Tooltip,
	},

	data() {
		return {
			loadingCounter:  0,
			userList:        [],
			statusText:      '',
			showLoginButton: false,
		}
	},

	computed: {
		authUrl() {
			return this.$buildURL(`/oidc/authorize/${this.$route.params.providerName}`, {apiRoute: true});
		},

		providers() {
			return {
				webkontor: {
					signInText: this.$t('OpenIDConnect.Webkontor.SignIn'),
				},
				frejaeid: {
					signInText: this.$t('OpenIDConnect.FrejaeID.SignIn'),
				},
			}
		},

		isFreja() {
			return this.providerName === 'frejaeid';
		},

		providerName() {
			const {providerName} = this.$route.params;

			return providerName;
		},

		provider() {
			return this.providers[this.providerName];
		},

		dvCodeTranslations() {
			return {
				'40322': this.$t('OpenIDConnect.NoAccessToPortal'),
				'40321': this.$t('OpenIDConnect.NoAccessToPortal'),
				'40320': this.$t('OpenIDConnect.NoAccessToPortal'),
			};
		},
	},

	mounted() {
		if(this.provider) {
			this.fetchUserList();
		} else {
			this.statusText = this.$t('OpenIDConnect.UnknownProvider');
		}
	},

	methods: {
		...mapActions(['setAuthenticated']),
	
		onCancel() {
			this.$tryCatch({
				task: async() => {
					this.showLoginButton = true;
					this.userList = [];
					this.statusText = '';
					await this.$http.get('/oidc/cancel', {skipBackendMessageSnackbar: true});

					if(this.isFreja) {
						this.$router.push({path: '/'});
					}
				},
			});
		},

		fetchUserList() {
			this.$tryCatch({
				task: async() => {
					this.loadingCounter++;
					this.showLoginButton = false;
					this.userList = [];
					this.userList = await this.$http.get(`/oidc/userlist`, {skipBackendMessageSnackbar: true});

					if(this.userList.length === 1 && this.userList[0].Roll === 'PER') {
						this.selectUser(this.userList[0]);
					}
				},
				finally: () => this.loadingCounter--,
				error:   (error) => {
					if(error.status === 404) {
						this.showLoginButton = true;
					} else if(error.DVCode === 40998) {
						this.statusText = this.$t('OpenIDConnect.NoAccountsExistForYourAccount');
						this.$msgError(this.statusText);
					} else {
						this.statusText = this.$t('OpenIDConnect.FailedToFetchUsers');
						this.$msgError(this.statusText);
					}

					if(this.isFreja) {
						this.$router.push({path: '/'});
					}
				},
			});
		},

		selectUser(user) {
			this.$tryCatch({
				task: async () => {
					this.loadingCounter++;
					const {
						UserAccount,
						UserAppSettings,
						FtgInfo,
						Auth,
						HasLicensOption,
						SalaryYears,
						HasPlus,
						HasWebLon,
						HasWebLonPlus,
						EnforceBankIDAuth,
					} = await this.$http.post('/oidc/login', {
						State: this.$route.query.state,
						Login: user.Login,
					}, {skipBackendMessageSnackbar: true});

					if(UserAppSettings && ['dark', 'white'].includes(UserAppSettings.Theme)) {
						this.$vuetify.theme.dark = UserAppSettings.Theme === 'dark';
					}

					this.setAuthenticated({UserAccount, UserAppSettings, FtgInfo, Auth, HasLicensOption, SalaryYears, HasPlus, HasWebLon, HasWebLonPlus, EnforceBankIDAuth});
					this.$router.push({name: 'Dashboard'});
				},
				finally: () => this.loadingCounter--,
				error:   ({DVCode}) => {
					this.userList = [];
					this.showLoginButton = true;
					this.$msgError(this.dvCodeTranslations[DVCode] ||this.$t('OpenIDConnect.LoginFailed'));
				},
			});
		},
	},
}
</script>
